<script setup lang="ts">
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinDescriptionGeolocation } from "~/modules/dtwin/models/DtwinDescriptionGeolocation.interface";
import type { DynamicUIGroup } from "~/modules/dtwin/models/DynamicUIGrouped.interface";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";

interface Props {
  modelValue: DtwinDescriptionGeolocation;
  isAllowedToEdit: boolean;
  deviceModelUiConfig?: DynamicUIGroup;
  dtwin: Dtwin;
}

const props = defineProps<Props>();
const emit = defineEmits(["update:address"]);

const { t } = useI18n();

const addressFieldRef = ref();
const address: Ref<string> = ref(props.modelValue.address);
const isEditing: Ref<boolean> = ref(false);
const isSending: Ref<boolean> = ref(false);
const isEdited: ComputedRef<boolean> = computed(
  () => addressFieldRef.value?.hasValueChanged ?? false,
);

// When the request to the backend succeeds, reset the editing flag to true
watch(
  () => props.modelValue,
  (modelValue) => {
    if (modelValue.doesUpdateSucceed) {
      isEditing.value = false;
      address.value = modelValue.address;
    }
    isSending.value = false;
  },
);

function onSave(newValue: string): void {
  isSending.value = true;
  emit("update:address", newValue);
}

// activate the leave page confirm message if edited
watch(
  () => isEdited.value,
  (isEdited) => useConfirmLeavePageStore().activate(isEdited),
);
</script>

<template>
  <block-container
    :title="t(`dtwins.form.description.geolocation.title`)"
    with-top-separator
  >
    <template #left>
      <template
        v-for="item in deviceModelUiConfig?.details?.description?.geolocation
          ?.left"
        :key="item.id"
      >
        <editable-field
          v-if="item.id === 'geolocation' && isAllowedToEdit"
          ref="addressFieldRef"
          v-model:is-editing="isEditing"
          :field-value="address"
          :label="item.label || ''"
          :is-saving="isSending"
          class="mb-4"
          @validate="onSave"
        />
        <dtwin-form-field v-else :item="item" :dtwin="dtwin" />
      </template>
    </template>
    <template
      v-if="deviceModelUiConfig?.details?.description?.geolocation.right"
      #right
    >
      <dtwin-form-field
        v-for="item in deviceModelUiConfig?.details?.description?.geolocation
          ?.right"
        :key="item.id"
        :item="item"
        :dtwin="dtwin"
      />
    </template>
  </block-container>
</template>

<style lang="scss">
// This css property is define here because tailwind css doesn't allow to target a grandchildren
.default-icon {
  svg {
    path:nth-child(2) {
      color: white;
    }
  }
}
</style>
